// Uploader 2.0

import { useForm } from "antd/es/form/Form";
import {
	DEFAULT_FIELD_NAME,
	DEFAULT_TIMEOUT,
	DURATION,
	FAILED,
	IN_PROGRESS,
	LIMITATION_ERROR_TYPE,
	LOCATION_COLLECT_METHOD,
	NONE,
	REUSED_FIELD_NAME,
	SUCCEEDED,
	UPLOADER_TYPE,
} from "constants/common";
import { NAV_ACTIONS, SECTION_TYPE } from "constants/DynamicWidget";
import errorMsgs from "constants/errorMsgs";
import {
	DEFAULT_GOOGLE_DRIVE_REQUEST_DATA,
	EMAIL_OPT_IN,
	WIDGET_FORM_STATE,
	WIDGET_GALLERY_POSITION,
} from "constants/widgetBuilder";
import DynamicWidgetContext from "contexts/DynamicWidget";
import { debounce, isEmpty } from "lodash";
import { ApiResponseTypes } from "models/Common";
import { TermTypes, WidgetSectionTypes } from "models/DynamicWidget";
import {
	GoogleRequestDataTypes,
	SelectedFileTypes,
	SendEmailPayloadTypes,
	TikTokUserTypes,
	UploadedContentTypes,
	UploadProgressParamTypes,
	UploadRequestPayloadTypes,
	WidgetDetailTypes,
} from "models/Widget";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { postRequest } from "services/api";
import { useUploadProgress } from "services/customHook";
import { formatFormValues } from "services/dynamicWidget";
import { generateFieldKey, getUrlS3 } from "services/funcHelper";
import { getBrowserGeolocation } from "services/location";
import utils from "services/utils";
import {
	checkedAllTerms,
	convertUploadPayload,
	convertUploadPayloadToFormData,
	creatorOptInEmailValues,
	generateApiURL,
	getIPStack,
	getReplacedFieldValuesOptIn,
	getSocialUsernameFromURL,
	getTermsIds,
	handleClearedValues,
	onUploadProgress,
	setClientId,
	showErrorMsg,
} from "services/widgetBuilder";
import {
	cancelRequest,
	createContentEnd,
	createContentRequest,
	getGeoLocationRequest,
	getLocationEnd,
	getLocationRequest,
	sendEmailRequest,
	setUploadProgress,
	storeData,
	uploadContentRequest,
	uploadGoogleDriveContentRequest,
	uploadGooglePhotosContentRequest,
	uploadYouTubeContentRequest,
	validateCampaignRequest,
} from "stores/actions";
import { StyledForm } from "styled-components/Common/CommonStyled";
import {
	StyledDynamicWidgetFormWrapper,
	StyledDynamicWidgetWrapper,
} from "styled-components/DynamicWidget/DynamicWidgetStyled";
import CancelUpload from "../UploadProgress/CancelUpload";
import UploadProgress from "../UploadProgress/UploadProgress";
import WidgetGallery from "../WidgetBuilder/WidgetGallery";
import WidgetHeader from "../WidgetBuilder/WidgetHeader";
import ActionButton from "./ActionButton";
import WelcomeMessage from "./WelcomeMessage";
import WidgetFooterSection from "./WidgetFooterSection";
import WidgetSection from "./WidgetSection";
import WidgetTermsSection from "./WidgetTermsSection";
import WidgetUploadConfirmation from "./WidgetUploadConfirmation";

type DynamicWidgetProps = {
	widgetDetails: WidgetDetailTypes;
};

const DynamicWidget = (props: DynamicWidgetProps) => {
	const dispatch = useDispatch();
	const { widgetDetails } = props;
	const [form] = useForm();
	const { t } = useTranslation();
	const {
		clientId,
		id: widgetId,
		metadata = "",
		terms,
		termsTemplateId,
		widgetSettingsDTO,
	} = widgetDetails || {};

	const { header, colors, body, gallery } = metadata || {};
	const {
		sections = [],
		emailOptIn = {},
		moreInfoTerms: { pageBreak: termPageBreak = false } = {},
	} = body || {};
	const { bgUrl = "", bgColor = "", formBackground = "" } = colors || {};
	const { srcLogo = "", sizeLogo = {} } = header || {};
	const { name: emailOptInType = "", fieldValues } = emailOptIn || {};
	const { timeoutDuration, locationCollectMethod } = widgetSettingsDTO || {};
	const { position } = gallery || {};
	const isMobile = utils.useDevice();

	const {
		clientDetail = {},
		campaign,
		uploadLocation,
		getLocationStatus,
		fileProgress,
		isCancelledProgressModal = false,
		createContentStatus,
		createdContentError,
		createdContent,
		uploadedContentListResult,
		uploadSocialContentStatus,
		uploadSocialContentError,
	} = useSelector((state: any) => state.widgets);
	const { youTubeAccount } = useSelector((state: any) => state.googleStore);
	const { brandName = "", orgType = "" } = clientDetail || {};

	const pathLocation = useLocation();
	const { search } = pathLocation;
	const campaignId = utils.getChallengeId(search); // campaignId
	const selectId = utils.getSelectId(search);

	const [activeUploadType, setActiveUploadType] = useState<string>(""); // Local files, TikTok, YouTube, Google Drive, Google  Photos
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [localFileList, setLocalFileList] = useState<SelectedFileTypes[]>([]);
	const [formValues, setFormValues] = useState<UploadRequestPayloadTypes | null>(null); // save values entered from form
	const [termList, setTermList] = useState<TermTypes[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false); // is used to check terms - show arrow if a term is not checked
	const [uploadContentPayloadList, setUploadContentPayloadList] = useState<FormData[]>([]);
	const [isCancelUpload, setIsCancelUpload] = useState(false);
	const [googleRequestData, setGoogleRequestData] = useState<GoogleRequestDataTypes>(
		DEFAULT_GOOGLE_DRIVE_REQUEST_DATA,
	);
	const [formState, setFormState] = useState<string>();
	const [modalType, setModalType] = useState<string>("");
	const [tikTokUser, setTikTokUser] = useState<TikTokUserTypes>();
	const [selectedSocialVideoList, setSelectedSocialVideoList] = useState<SelectedFileTypes[]>([]); // TikTok, YouTube video(s)

	const { isInProgress, isCompleteUpload, isInError } = useUploadProgress();

	useEffect(() => {
		if (campaignId) {
			dispatch(
				validateCampaignRequest({
					clientId,
					campaignId,
				}),
			);
		}
	}, []);

	useEffect(() => {
		if (widgetDetails) {
			initTerms();
		}
	}, [widgetDetails]);

	useEffect(() => {
		if (getLocationStatus === SUCCEEDED) {
			form.submit();
		}
		if (getLocationStatus === SUCCEEDED || getLocationStatus === FAILED) {
			dispatch(getLocationEnd());
		}
	}, [getLocationStatus]);

	useEffect(() => {
		if (createContentStatus === SUCCEEDED && createdContent) {
			startUploadingContent(createdContent);
		}

		if (createContentStatus === FAILED && createdContentError) {
			showErrorMsg(createdContentError, 6);
			dispatch(createContentEnd());
		}
	}, [createContentStatus, createdContent, createdContentError]);

	useEffect(() => {
		if (uploadSocialContentStatus === SUCCEEDED) {
			setFormState(WIDGET_FORM_STATE.UPLOAD_CONFIRMATION);
			resetDataInStore();

			if (activeUploadType === UPLOADER_TYPE.GOOGLE_DRIVE) {
				setGoogleRequestData(DEFAULT_GOOGLE_DRIVE_REQUEST_DATA);
			}
		}

		if (uploadSocialContentStatus === FAILED && uploadSocialContentError) {
			showErrorMsg(uploadSocialContentError);
		}

		if (uploadSocialContentStatus === FAILED || uploadSocialContentStatus === SUCCEEDED) {
			dispatch(
				storeData({
					uploadSocialContentStatus: NONE,
					uploadSocialContentError: null,
				}),
			);
		}
	}, [uploadSocialContentStatus, uploadSocialContentError]);

	useEffect(() => {
		if (isCompleteUpload) {
			// setTimeout is used to user checking error
			setTimeout(
				() => {
					if (uploadedContentListResult.length && !isCancelledProgressModal) {
						setFormState(WIDGET_FORM_STATE.UPLOAD_CONFIRMATION);
						resetDataInStore();
					}
				},
				fileProgress.some((item: UploadRequestPayloadTypes) => item.limitationError)
					? DURATION.S_3
					: DURATION.S_0,
			);
		}
	}, [isCompleteUpload, isCancelledProgressModal]);

	const handleSendEmail = () => {
		if (formValues?.email) {
			const payloadSendEmail: SendEmailPayloadTypes = {
				email: formValues?.email,
				requestIds: [],
				termsIds: getTermsIds(terms),
				widgetUrl: window.location.href,
			};
			if (uploadedContentListResult.length) {
				payloadSendEmail.requestIds = uploadedContentListResult.map(
					(item: UploadedContentTypes) => item.requestId,
				);
			}
			if (campaignId) {
				payloadSendEmail.campaignId = campaignId;
			}
			dispatch(sendEmailRequest(payloadSendEmail));
		}
	};

	useEffect(() => {
		if (isCompleteUpload) {
			if (uploadedContentListResult.length) {
				handleSendEmail();
			}
			setIsCancelUpload(false);
		}
	}, [isCompleteUpload, uploadedContentListResult]);

	const startUploadingContent = async (createdContent: any) => {
		const contentList = convertUploadPayload(
			createdContent,
			widgetDetails,
			campaignId,
			uploadContentPayloadList,
		);

		if (contentList.length) {
			contentList.forEach((content) => {
				dispatch(
					uploadContentRequest({
						...content,
						onUploadProgress: (params: UploadProgressParamTypes) =>
							onUploadProgress(params, dispatch),
						widgetInfo: widgetDetails,
						isNewFlow: widgetDetails?.widgetSettingsDTO?.isNewFlow,
					}),
				);
			});
		}
	};

	const isFileListEmpty = useMemo(() => {
		return (
			!localFileList.length &&
			!selectedSocialVideoList.length &&
			!googleRequestData?.selectedMediaList?.length
		);
	}, [localFileList, selectedSocialVideoList, googleRequestData]);

	const initTerms = () => {
		let termsTemp = [];
		const termsConditionsTemp = terms ? [...terms] : [];

		if (
			emailOptIn &&
			emailOptIn.fieldValues &&
			emailOptInType === EMAIL_OPT_IN.ASK_USER.name &&
			emailOptIn.fieldValues.fieldName === EMAIL_OPT_IN.FIELD_NAME
		) {
			termsConditionsTemp.push(getReplacedFieldValuesOptIn(fieldValues, brandName) as TermTypes);
		}

		termsTemp = termsConditionsTemp.map((item) => {
			return {
				...item,
				required: item.isTermsMandated || false,
				checked: false,
				isAgreed: false,
			};
		});

		setTermList([...termsTemp]);
	};

	const initializeMailingAddress = () => {
		if (formValues?.mailingAddress) {
			form.setFieldsValue({
				mailingAddress: formValues?.mailingAddress?.description || undefined,
			});
		}
	};

	const navigatePage = (action: string) => {
		if (!validateSelectedFiles()) return;

		if (action === NAV_ACTIONS.NEXT) {
			form
				.validateFields()
				.then(() => {
					initializeMailingAddress();
					setPageIndex(pageIndex + 1);
				})
				.catch((e) => {
					console.log(e);
				});
		} else if (action === NAV_ACTIONS.BACK) {
			initializeMailingAddress();
			setPageIndex(pageIndex - 1);
		}
	};

	// Only clear current fields shown on the screen
	const clearForm = () => {
		const clearedValues: UploadRequestPayloadTypes = handleClearedValues(currentSections);
		if (pagePosition.isFirst) {
			setLocalFileList([]);
			setGoogleRequestData({ ...googleRequestData, selectedMediaList: [] });
			setSelectedSocialVideoList([]);
		}

		if (pagePosition.isLast) {
			setTermList(termList.map((term) => ({ ...term, checked: false })));
		}

		form.setFieldsValue({
			...clearedValues,
		});
		setFormValues({ ...formValues, ...clearedValues });
	};

	const setFieldsValue = (values: any) => {
		form.setFieldsValue({ ...values });
	};

	const onValuesChange = useCallback(
		debounce((changedValues) => {
			const [fieldName] = Object.keys(changedValues);
			if (fieldName === DEFAULT_FIELD_NAME.mailingAddress) {
				return;
			}
			setFormValues({ ...(formValues || {}), ...changedValues });
		}, 300),
		[formValues],
	);

	const socialUsernameFromURL = useMemo(() => {
		const socialUsernameTemp = getSocialUsernameFromURL(search);
		return utils.removeEmptyValueObj(socialUsernameTemp);
	}, []);

	const validFileList = useMemo(() => {
		return localFileList.filter((item) => !item.error).map((item) => item.file);
	}, [localFileList]);

	const formattedValues = useMemo(() => {
		return formValues ? formatFormValues(formValues, sections) : {};
	}, [formValues, sections]);

	const onFinish = async () => {
		// Reset data before implementing new upload
		if (isCancelledProgressModal || fileProgress.length || uploadedContentListResult.length) {
			dispatch(
				storeData({
					isCancelledProgressModal: false,
					uploadedContentListResult: [],
				}),
			);
		}

		const timeout = timeoutDuration || DEFAULT_TIMEOUT;
		let hasEntribeStandardTerms = false;
		let hasCustomTerms = false;

		for (const item of termList) {
			if (!item.fieldName && item.checked) {
				if (item.isEntribeStandardTerms) hasEntribeStandardTerms = true;
				else hasCustomTerms = true;

				if (hasEntribeStandardTerms && hasCustomTerms) break;
			}
		}

		let requestData = {
			widgetId,
			clientId,
			selectId,
			challengeId: 0,
			location: uploadLocation,
			timeout: utils.convertTimeout(timeout),
			termsIds: getTermsIds(termList),
			orgType,
			brandName,
			hasEntribeStandardTerms,
			hasCustomTerms,
			termsTemplateId,
			uploadWidgetLink: utils.formatUploadWidgetLink(
				widgetDetails.url,
				{
					selectId,
					challengeId: campaignId,
				},
				socialUsernameFromURL,
			),
			...formattedValues,
			...socialUsernameFromURL, // Replace social usernames attached in the URL
		} as UploadRequestPayloadTypes;

		if (campaign) {
			requestData.challengeId = campaign.id;
		}

		if (emailOptInType && emailOptInType !== EMAIL_OPT_IN.NOT_ADD_USER.name) {
			requestData.creatorOptInEmail = {
				...creatorOptInEmailValues(emailOptIn, terms),
				lastWidgetId: widgetId,
			};
		}

		if (locationCollectMethod !== LOCATION_COLLECT_METHOD.NO_COLLECT) {
			requestData.ipStack =
				locationCollectMethod === LOCATION_COLLECT_METHOD.COLLECT_WITH_CONSENT
					? uploadLocation
					: getIPStack(uploadLocation);
		}

		// Replace social usernames linked to get list
		if (tikTokUser && tikTokUser?.username) {
			requestData[REUSED_FIELD_NAME.tiktok] = tikTokUser.username;
		}

		if (youTubeAccount && youTubeAccount.username) {
			requestData[REUSED_FIELD_NAME.youtube] = youTubeAccount.username;
		}

		requestData = utils.parseUploadData(requestData) as any; // Clean data

		const requestPayload: FormData[] = await convertUploadPayloadToFormData(
			requestData,
			localFileList.filter((item) => !item.error).map((item) => item.file),
			uploadLocation,
		);

		if (
			activeUploadType === UPLOADER_TYPE.GOOGLE_DRIVE ||
			activeUploadType === UPLOADER_TYPE.GOOGLE_PHOTOS
		) {
			const validList = googleRequestData?.selectedMediaList.filter(
				(item) => isEmpty(item?.limitationError) || !item?.limitationError,
			);

			if (validList.length) {
				const requestPayload = {
					...requestData,
					accessToken: googleRequestData?.accessToken,
					...(activeUploadType === UPLOADER_TYPE.GOOGLE_DRIVE
						? { fileRequests: validList }
						: { mediaItems: validList }),
				};

				dispatch(
					activeUploadType === UPLOADER_TYPE.GOOGLE_DRIVE
						? uploadGoogleDriveContentRequest(requestPayload)
						: uploadGooglePhotosContentRequest(requestPayload),
				);
			} else {
				showErrorMsg(t("message.valid_file_attached"));
			}

			return;
		}

		if (activeUploadType === UPLOADER_TYPE.TIKTOK && selectedSocialVideoList.length) {
			const updateStatus = (status: number) => {
				dispatch(
					storeData({
						uploadSocialContentStatus: status,
					}),
				);
			};

			const successCallback = () => {
				setFormState(WIDGET_FORM_STATE.UPLOAD_CONFIRMATION);
				updateStatus(NONE);
			};

			const failedCallback = (resp: ApiResponseTypes) => {
				const errorMsg = resp.error ? resp.error : errorMsgs["en"].uploadFailed;
				showErrorMsg(errorMsg);
				updateStatus(NONE);
			};

			// Show loading for submit button
			updateStatus(IN_PROGRESS);

			const url = generateApiURL(requestData, activeUploadType, widgetId);
			const shareUrls = selectedSocialVideoList.map((video: any) => encodeURI(video.share_url));
			postRequest(
				url,
				{ ...requestData, shareUrls },
				undefined,
				undefined,
				undefined,
				undefined,
				successCallback,
				failedCallback,
			);

			return;
		}

		if (activeUploadType === UPLOADER_TYPE.YOUTUBE && selectedSocialVideoList.length) {
			const listVideoIds = selectedSocialVideoList.map((item: any) => item.id);
			const requestPayload = {
				...requestData,
				listVideoIds,
			};
			dispatch(uploadYouTubeContentRequest(requestPayload));
			return;
		}

		setUploadContentPayloadList(requestPayload); // is used for API uploading content
		dispatch(createContentRequest({ ...requestData }));
	};

	const validateSelectedFiles = () => {
		if (isFileListEmpty) {
			showErrorMsg(t("message.no_file_attached"));
			return false;
		}

		if (
			(localFileList.length && !validFileList.length) ||
			(googleRequestData.selectedMediaList.length &&
				!googleRequestData.selectedMediaList.filter(
					(item) => !item.limitationError || isEmpty(item.limitationError),
				).length)
		) {
			showErrorMsg(t("message.no_valid_file_attached"));
			return false;
		}

		return true;
	};

	const handleSubmitButton = () => {
		setClientId(clientDetail);

		if (!validateSelectedFiles() && paginationData.length === 1) {
			return;
		}

		form
			.validateFields()
			.then(() => {
				if (checkedAllTerms(termList)) {
					handleLocationCollect();
				} else {
					setIsSubmitting(true);
				}
			})
			.catch(() => {});
	};

	const showPreparingModal = async () => {
		const payload = await convertUploadPayloadToFormData({}, validFileList, uploadLocation);

		const campaignId = utils.getChallengeId(search);
		const tempCreatedContent = { id: 0, selectId };
		const contentList = convertUploadPayload(
			tempCreatedContent,
			widgetDetails,
			campaignId,
			payload,
		);

		if (contentList.length) {
			const progressPayloadList = contentList.map((content) => {
				const dataObject = utils.convertFormDataToObject(content.formData);
				const { progress = 0, fileProgressId, searchParams } = content;

				return {
					...dataObject,
					fileProgressId,
					progress,
					remainingTime: 0,
					requestId: searchParams?.requestId,
					status: FAILED,
				};
			});

			progressPayloadList.forEach((payload) => {
				dispatch(setUploadProgress(payload));
			});
		}
	};

	const handleLocationCollect = () => {
		showPreparingModal();
		const isExistUploadLocation = uploadLocation && !isEmpty(uploadLocation);

		const successCallback = (position: string) => {
			if (position) {
				dispatch(getGeoLocationRequest(position));
			}
		};
		const onSubmit = () => {
			form.submit();
		};

		if (locationCollectMethod !== LOCATION_COLLECT_METHOD.NO_COLLECT) {
			if (locationCollectMethod === LOCATION_COLLECT_METHOD.COLLECT_WITH_CONSENT) {
				getBrowserGeolocation(
					(position: string) => (isExistUploadLocation ? onSubmit() : successCallback(position)),
					onSubmit,
				);
			} else if (locationCollectMethod === LOCATION_COLLECT_METHOD.COLLECT_WITHOUT_CONSENT) {
				isExistUploadLocation ? onSubmit() : dispatch(getLocationRequest());
			}
		} else {
			onSubmit();
		}
		setIsSubmitting(false);
	};

	const onCancelUpload = () => {
		if (isInProgress && !isInError) {
			setIsCancelUpload(true);
		} else {
			resetDataInStore();
		}
	};

	const resetDataInStore = () => {
		const resetValues = {
			createdContent: null,
			createContentStatus: NONE,
			isCancelledProgressModal: true,
			uploadSocialContentResult: [],
			fileProgress: [],
		};

		dispatch(storeData(resetValues));
	};

	const resetState = () => {
		setPageIndex(0);
		setLocalFileList([]);
		setSelectedSocialVideoList([]);
		setGoogleRequestData({ selectedMediaList: [], accessToken: "" });
		setActiveUploadType("");
	};

	const galleryPosition = useMemo(() => {
		return {
			isAbove: position === WIDGET_GALLERY_POSITION.ABOVE,
			isBelow: position === WIDGET_GALLERY_POSITION.BELOW,
		};
	}, [position]);

	const confirmCancel = () => {
		if (fileProgress.length) {
			fileProgress.forEach((item: UploadRequestPayloadTypes) => {
				if (item.status === IN_PROGRESS)
					dispatch(
						cancelRequest({
							fileProgressId: item.fileProgressId,
							isNotRemove: true,
							actionType: LIMITATION_ERROR_TYPE.CANCEL,
						}),
					);
			});
		}

		resetState();
		resetDataInStore();
		setIsCancelUpload(false);
	};

	const handleUploadMore = () => {
		setFormState("");
		setActiveUploadType("");
		resetState();
		resetDataInStore();
		initializeMailingAddress();

		setTimeout(() => {
			(document.documentElement || document.body).scrollTo({
				top: 0,
				behavior: "smooth",
			});
		}, 0);
	};

	const termComponent = (
		<WidgetTermsSection
			widgetDetails={widgetDetails}
			termList={termList}
			setTermList={setTermList}
			isSubmitting={isSubmitting}
			setIsSubmitting={setIsSubmitting}
		/>
	);

	const paginationData = useMemo(() => {
		const listTemp: WidgetSectionTypes[][] = [];
		sections.forEach((item: WidgetSectionTypes, index: number) => {
			if (index === 0 || item?.pageBreak) {
				listTemp.push([item]);
			} else {
				listTemp[listTemp.length - 1].push(item);
			}
		});

		if (termPageBreak) {
			listTemp.push([
				{
					key: generateFieldKey(),
					type: SECTION_TYPE.TERMS,
				},
			]);
		}

		return listTemp;
	}, [sections, termPageBreak]);

	const pagePosition = useMemo(() => {
		return {
			isFirst: pageIndex === 0,
			isLast: pageIndex === paginationData.length - 1,
		};
	}, [pageIndex, paginationData]);

	const currentSections = useMemo(() => {
		return paginationData[pageIndex];
	}, [paginationData, pageIndex]);

	const contextValues = {
		currentSections,
		navigatePage,
		localFileList,
		setLocalFileList,
		widgetDetails,
		pagePosition,
		clearForm,
		setFieldsValue,
		formValues,
		setFormValues,
		handleSubmitButton,
		googleRequestData,
		setGoogleRequestData,
		setActiveUploadType,
		activeUploadType,
		modalType,
		setModalType,
		setTikTokUser,
		tikTokUser,
		selectedSocialVideoList,
		setSelectedSocialVideoList,
		isFileListEmpty,
	};

	return (
		<DynamicWidgetContext.Provider value={contextValues}>
			<StyledDynamicWidgetWrapper
				id="widget-wrapper"
				bgUrl={getUrlS3(bgUrl)}
				bgColor={bgColor}
				isMobile={isMobile}>
				<StyledDynamicWidgetFormWrapper
					background={formBackground}
					isMobile={isMobile}
					minHeight={formState ? "100svh" : undefined}>
					<WidgetHeader sizeLogo={sizeLogo} srcLogo={getUrlS3(srcLogo)} />
					{!formState && (
						<>
							{galleryPosition.isAbove && pagePosition.isFirst && (
								<WidgetGallery
									detailWidget={widgetDetails}
									isMoreField={false}
									isMobile={isMobile}
								/>
							)}
							{pagePosition.isFirst && <WelcomeMessage header={header} />}
							{galleryPosition.isBelow && pagePosition.isFirst && (
								<WidgetGallery
									detailWidget={widgetDetails}
									isMoreField={false}
									isMobile={isMobile}
								/>
							)}
							<StyledForm
								form={form}
								layout="vertical"
								requiredMark={false}
								onValuesChange={onValuesChange}
								onFinish={onFinish}>
								{currentSections[0]?.type === SECTION_TYPE.TERMS ? (
									<>{termComponent}</>
								) : (
									currentSections.map((section: WidgetSectionTypes) => {
										return <WidgetSection key={section.key} section={section} />;
									})
								)}
								{pagePosition.isLast && !termPageBreak && termComponent}
								<ActionButton />
							</StyledForm>
						</>
					)}
					{formState === WIDGET_FORM_STATE.UPLOAD_CONFIRMATION && (
						<WidgetUploadConfirmation
							widgetDetails={widgetDetails}
							handleUploadMore={handleUploadMore}
						/>
					)}

					{!formState && <WidgetFooterSection widgetDetails={widgetDetails} />}
				</StyledDynamicWidgetFormWrapper>
			</StyledDynamicWidgetWrapper>

			{!!fileProgress.length && !isCancelledProgressModal && (
				<UploadProgress
					visible={!!fileProgress.length && !isCancelledProgressModal}
					onCancelUpload={onCancelUpload}
					widgetInfo={widgetDetails}
					isNewLayout={true}
				/>
			)}

			{widgetDetails && (
				<CancelUpload
					visible={isCancelUpload}
					onConfirm={confirmCancel}
					bgSubmit={widgetDetails?.metadata?.colors.bgSubmit}
					onCancel={() => {
						setIsCancelUpload(false);
					}}
					isNewLayout={true}
				/>
			)}
		</DynamicWidgetContext.Provider>
	);
};

export default DynamicWidget;
